import { useCallback, useEffect, useRef, type ReactNode } from "react";
import { useFocusTrap } from "@/hooks/use-focus-trap";

interface DialogShellProps {
  open: boolean;
  onClose: () => void;
  ariaLabelledBy: string;
  maxWidth?: string;
  /** Override the inner panel className (default: standard p-6 card) */
  panelClassName?: string;
  children: ReactNode;
}

/**
 * Shared dialog shell.
 * Handles overlay, escape key, click-outside, focus trap, and aria attributes.
 */
export function DialogShell({
  open,
  onClose,
  ariaLabelledBy,
  maxWidth = "max-w-lg",
  panelClassName,
  children,
}: DialogShellProps) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  useFocusTrap(dialogRef, open);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      // An inner popup (a combobox listbox) that consumed the press owns it —
      // the dialog is dismissed only by an Escape nobody else handled.
      if (e.defaultPrevented) return;
      if (e.key === "Escape") onClose();
    },
    [onClose],
  );

  useEffect(() => {
    if (open) {
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }
  }, [open, handleKeyDown]);

  if (!open) return null;

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={(e) => {
        if (e.target === overlayRef.current) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby={ariaLabelledBy}
    >
      <div
        ref={dialogRef}
        className={
          panelClassName ??
          `w-full ${maxWidth} max-h-[90vh] overflow-y-auto rounded-lg bg-white p-6 shadow-xl dark:bg-gray-800`
        }
      >
        {children}
      </div>
    </div>
  );
}
